/**
 * QualityDiscountFormDialog — New / Edit for Virtual Warehouse › Quality Discount.
 * Converted 1:1 from Access `frmVirtualFactoryQryQualityDiscountAdd` (title
 * "Quality Discount"). Composite key = (warehouse, qualityCode); both combos are
 * DISABLED on Edit. Two grade sections — "Discount Rate" (22 fields) and "Permit"
 * (22 fields) — all plain numeric TextBoxes (2 decimals, right-aligned).
 *
 * Validation (verbatim Access):
 *   warehouse blank   → "Please choose warehouse!"
 *   qualityCode blank → "Please choose quality_code!"
 *   New pair dup      → "'Warehouse' and 'Quality code' existed! Please enter a new pair!"
 * There is NO duplicate-check endpoint, so the pair-dup rule is enforced by the
 * backend (create → 422); its detail is surfaced via snackbar. Blank guards are
 * client-side.
 *
 * Hydrate (Edit): props expose only `recordId` and there is no get-by-id endpoint,
 * so the row is read from the SAME cached list the parent view already loaded
 * (`useGetQualityDiscountListQuery({page:1,pageSize:ALL_ROWS_PAGE_SIZE})`) via
 * `selectFromResult` + find(id). Selection can only happen on a loaded row.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import {
  type LookupItem,
  type QualityDiscountRow,
  type QualityDiscountWrite,
  useCreateQualityDiscountMutation,
  useUpdateQualityDiscountMutation,
  useGetQualityDiscountListQuery,
  useVwCpCodeFirstLookupQuery,
  useVwQualityLookupQuery,
} from '../../store/api/virtualWarehouseApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit'

interface Props {
  open: boolean
  mode: Mode
  recordId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

// Match the args the parent view uses so we hit the same cache entry.
const ALL_ROWS_PAGE_SIZE = 1_000_000

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const grid3Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const grid4Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

/* ───────── Reusable flat combo ───────── */
function Combo(props: {
  label: string
  value: string
  valueLabel?: string
  options: LookupItem[]
  loading?: boolean
  disabled?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string, item: LookupItem | null) => void
}) {
  const { label, value, valueLabel, options, loading, disabled, onInputChange, onPick } = props
  const sel = value ? options.find((o) => o.value === value) ?? { value, label: valueLabel || value } : null
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  const getLabel = (o: LookupItem) => (!o ? '' : o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)
  return (
    <Autocomplete
      size={SM}
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '', v)}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  )
}

/* Grades (Access order) + display labels; used for both DiscountRate & Permit. */
const GRADE_KEYS = [
  'mc', 'bb', 'black', 'brokens', 'fm', 'foreign', 'mouldy', 'brown', 'insect', 'cherry', 'white',
  'overdry', 'immature', 'old', 's18', 's17', 's16', 's15', 's14', 's13', 's12', 'underS12',
] as const
const GRADE_LABELS: Record<string, string> = {
  mc: 'MC', bb: 'BB', black: 'Black', brokens: 'Brokens', fm: 'FM', foreign: 'Foreign',
  mouldy: 'Mouldy', brown: 'Brown', insect: 'Insect', cherry: 'Cherry', white: 'White',
  overdry: 'Overdry', immature: 'Immature', old: 'Old', s18: 'S18', s17: 'S17', s16: 'S16',
  s15: 'S15', s14: 'S14', s13: 'S13', s12: 'S12', underS12: '<S12',
}
const rateKey = (g: string) => `${g}DiscountRate`
const permitKey = (g: string) => `${g}Permit`

type Draft = Record<string, string>
const EMPTY: Draft = (() => {
  const d: Draft = { warehouse: '', qualityCode: '' }
  GRADE_KEYS.forEach((g) => { d[rateKey(g)] = ''; d[permitKey(g)] = '' })
  return d
})()

export default function QualityDiscountFormDialog({ open, mode, recordId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const set = (k: string, v: string) => setDraft((p) => ({ ...p, [k]: v }))

  // Hydrate source: read the selected row from the parent's cached list.
  const { row } = useGetQualityDiscountListQuery(
    { page: 1, pageSize: ALL_ROWS_PAGE_SIZE },
    {
      skip: !open || mode === 'new' || recordId == null,
      selectFromResult: ({ data }) => ({ row: data?.items.find((r) => r.id === recordId) as QualityDiscountRow | undefined }),
    },
  )

  const [createQd, { isLoading: creating }] = useCreateQualityDiscountMutation()
  const [updateQd, { isLoading: updating }] = useUpdateQualityDiscountMutation()
  const saving = creating || updating

  const skip = { skip: !open }
  const [warehouseQ, setWarehouseQ] = useState('')
  const [qualityQ, setQualityQ] = useState('')
  const warehouses = useVwCpCodeFirstLookupQuery(warehouseQ || undefined, skip)
  const qualities = useVwQualityLookupQuery(qualityQ || undefined, skip)

  // Hydrate on open. Deps only [open, mode, recordId, row].
  useEffect(() => {
    if (!open) return
    if (mode === 'new') {
      setLabels({})
      setDraft({ ...EMPTY })
      return
    }
    if (!row) return
    // Bare name — the Warehouse combo prepends the code itself → "CODE — Name".
    setLabels({ warehouse: s(row.shortName) })
    const next: Draft = { ...EMPTY, warehouse: s(row.warehouse), qualityCode: s(row.qualityCode) }
    GRADE_KEYS.forEach((g) => {
      next[rateKey(g)] = fmtStd((row as unknown as Record<string, unknown>)[rateKey(g)])
      next[permitKey(g)] = fmtStd((row as unknown as Record<string, unknown>)[permitKey(g)])
    })
    setDraft(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, recordId, row])

  const warehouseBlank = !draft.warehouse.trim()
  const qualityBlank = !draft.qualityCode.trim()
  const canSave = canEdit && !warehouseBlank && !qualityBlank && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canEdit) return
    // Access blank guards (verbatim messages).
    if (warehouseBlank) { snack.error('Please choose warehouse!'); return }
    if (qualityBlank) { snack.error('Please choose quality_code!'); return }
    const payload: QualityDiscountWrite = {
      warehouse: draft.warehouse.trim(),
      qualityCode: draft.qualityCode.trim(),
    }
    GRADE_KEYS.forEach((g) => {
      (payload as Record<string, unknown>)[rateKey(g)] = parseNum(draft[rateKey(g)]);
      (payload as Record<string, unknown>)[permitKey(g)] = parseNum(draft[permitKey(g)])
    })
    try {
      if (mode === 'edit' && recordId != null) {
        await updateQd({ id: recordId, body: payload }).unwrap()
        snack.success('Quality Discount updated')
      } else {
        await createQd(payload).unwrap()
        snack.success('Quality Discount added')
      }
      onSaved()
      onClose()
    } catch (err) {
      // Pair-dup enforced by BE (422) — surface its detail (Access:
      // "'Warehouse' and 'Quality code' existed! Please enter a new pair!").
      snack.error(getErrorMessage(err, 'Save quality discount failed'))
    }
  }

  const title = mode === 'new' ? 'New' : 'Edit'
  const ro = !canEdit
  const keyLocked = ro || mode === 'edit' // composite key locked on Edit

  const numField = (key: string, label: string) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key]} disabled={ro}
      onChange={(e) => set(key, e.target.value)}
      onBlur={() => set(key, fmtStd(draft[key]))}
      inputProps={numProps}
    />
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>{title} — Quality Discount</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          {canEdit && (
            <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
              {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
          )}
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit quality discounts.</Alert>}

        {/* ── Key ── */}
        <Typography sx={sectionTitleSx}>Quality Discount</Typography>
        <Box sx={grid3Sx}>
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            <Combo label="Warehouse *" value={draft.warehouse} valueLabel={labels.warehouse} disabled={keyLocked}
              options={warehouses.data?.items ?? []} loading={warehouses.isFetching} onInputChange={setWarehouseQ}
              onPick={(v, it) => { set('warehouse', v); setLabels((p) => ({ ...p, warehouse: it?.label ?? '' })) }} />
          </Box>
          <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>
            <Combo label="Quality *" value={draft.qualityCode} disabled={keyLocked}
              options={qualities.data?.items ?? []} loading={qualities.isFetching} onInputChange={setQualityQ}
              onPick={(v) => set('qualityCode', v)} />
          </Box>
        </Box>

        {/* ── Discount Rate (22) ── */}
        <Typography sx={sectionTitleSx}>Discount Rate</Typography>
        <Box sx={grid4Sx}>
          {GRADE_KEYS.map((g) => (
            <Box key={`rate-${g}`}>{numField(rateKey(g), GRADE_LABELS[g])}</Box>
          ))}
        </Box>

        {/* ── Permit (22) ── */}
        <Typography sx={sectionTitleSx}>Permit</Typography>
        <Box sx={grid4Sx}>
          {GRADE_KEYS.map((g) => (
            <Box key={`permit-${g}`}>{numField(permitKey(g), GRADE_LABELS[g])}</Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
